/**
 * Viewer 独占的帧请求与视觉任务调度器。
 *
 * 不创建另一条 RAF：宿主既有动画循环调用 flushFrame，普通交互仅登记请求。同步截图等
 * 通过 renderImmediately 直接结算；准备、视觉、管线按固定顺序执行，渲染中的新请求不丢失。
 */
export class ViewerFrameScheduler implements ViewerVisualUpdateSchedulerPort, ViewerIdleRenderSchedulerPort {
  /** 当前未被提交的渲染请求。 */
  private renderRequested = false;
  /** 防止相机事件或渲染回调递归提交另一条管线。 */
  private processingFrame = false;
  /** 已释放后不再接受任务。 */
  private disposed = false;
  /** 本帧之后待消费的视觉任务，使用稳定所有者作键。 */
  private pendingVisualUpdates = new Map<object, () => void>();
  /** 双缓冲任务表；执行时新到的任务只进入 pending，不扩张当前迭代。 */
  private activeVisualUpdates = new Map<object, () => void>();
  /** 空闲帧任务不触发普通重绘请求，也不创建独立 RAF。 */
  private readonly idleRenderTasks = new Map<object, ViewerIdleRenderTask>();
  /** 宿主依赖在 dispose 时解除，避免残留调度器引用继续保留整个 Viewer。 */
  private options: ViewerFrameSchedulerOptions | null;
  /** 普通重绘请求累计数。 */
  private renderRequests = 0;
  /** 视觉更新登记累计数。 */
  private scheduledVisualUpdates = 0;
  /** 取消的待执行视觉更新累计数。 */
  private cancelledVisualUpdates = 0;
  /** 普通帧结算机会累计数。 */
  private frameFlushes = 0;
  /** 显式同步结算请求累计数。 */
  private immediateFlushes = 0;
  /** 真正执行的视觉更新回调累计数。 */
  private executedVisualUpdates = 0;
  /** 进入渲染管线的尝试累计数。 */
  private renderAttempts = 0;
  /** 成功提交的完整画面累计数。 */
  private renderedFrames = 0;

  /** @param options 相机/输入准备与实际画面提交端口。 */
  public constructor(options: ViewerFrameSchedulerOptions) {
    this.options = options;
  }

  /** 登记普通重绘请求，不在事件回调中执行 GPU 管线。 */
  public requestRender(): void {
    if (this.disposed) return;
    this.renderRequests += 1;
    this.renderRequested = true;
  }

  /**
   * 按所有者合并同帧视觉更新，并确保最终状态有一次重绘机会。
   * @param owner 视觉资源的生命周期所有者。
   * @param update 使用所有者当前最终状态刷新的稳定回调。
   */
  public scheduleVisualUpdate(owner: object, update: () => void): void {
    if (this.disposed) return;
    this.scheduledVisualUpdates += 1;
    this.pendingVisualUpdates.set(owner, update);
    this.requestRender();
  }

  /**
   * 清除指定所有者尚未执行的任务，不影响其他视觉通道。
   * @param owner 即将取消或释放的视觉所有者。
   */
  public cancelVisualUpdate(owner: object): void {
    if (this.pendingVisualUpdates.delete(owner)) this.cancelledVisualUpdates += 1;
    if (this.activeVisualUpdates.delete(owner)) this.cancelledVisualUpdates += 1;
  }

  /** @param owner 资源所有者。 @param task 在真实管线中预热一份派生资源的同步事务。 */
  public scheduleIdleRender(owner: object, task: ViewerIdleRenderTask): void {
    if (!this.disposed) this.idleRenderTasks.set(owner, task);
  }

  /** @param owner 即将取消或释放的预热所有者。 */
  public cancelIdleRender(owner: object): void {
    this.idleRenderTasks.delete(owner);
  }

  /** @returns 本帧是否真正提交了画面。 */
  public flushFrame(): boolean {
    if (!this.disposed) this.frameFlushes += 1;
    return this.processFrame(false);
  }

  /**
   * 同步提交当前状态，用于截图、导出及显式同步调用。
   * 重入时仅登记请求，当前管线退出后由下一帧结算，避免递归渲染破坏 Pass 状态。
   * @returns 此次调用是否真正提交了画面。
   */
  public renderImmediately(): boolean {
    if (!this.disposed) this.immediateFlushes += 1;
    if (this.processingFrame) {
      this.requestRender();
      return false;
    }
    return this.processFrame(true);
  }

  /**
   * 读取累计工作量快照。
   *
   * @returns 新建的只读数值对象；调用方不能重置调度器内部计数。
   */
  public getStats(): ViewerFrameSchedulerStats {
    return {
      renderRequests: this.renderRequests,
      scheduledVisualUpdates: this.scheduledVisualUpdates,
      cancelledVisualUpdates: this.cancelledVisualUpdates,
      frameFlushes: this.frameFlushes,
      immediateFlushes: this.immediateFlushes,
      executedVisualUpdates: this.executedVisualUpdates,
      renderAttempts: this.renderAttempts,
      renderedFrames: this.renderedFrames,
    };
  }

  /** 释放全部任务和宿主引用；由 Viewer 停止其自有动画循环。 */
  public dispose(): void {
    this.disposed = true;
    this.renderRequested = false;
    this.pendingVisualUpdates.clear();
    this.activeVisualUpdates.clear();
    this.idleRenderTasks.clear();
    this.options = null;
  }

  /**
   * 按“状态准备→最终视觉→画面”执行事务。
   * @param force 是否为显式同步提交。
   * @returns 是否提交完成；未就绪时保持脏请求。
   */
  private processFrame(force: boolean): boolean {
    if (this.disposed || this.processingFrame || !this.options) return false;
    const options = this.options;
    this.processingFrame = true;
    let idleTask: ViewerIdleRenderTask | undefined;
    let rendered = false;
    try {
      options.prepareFrame();
      if (this.disposed) return false;
      const updatedVisuals = this.flushVisualUpdates();
      if (this.disposed) return false;
      if (!force && !this.renderRequested && !updatedVisuals) {
        // 只在相机/输入/视觉均没有前台绘制请求时预热一项，避免首选与预热争抢同一帧。
        if (this.idleRenderTasks.size === 0) return false;
        const entry = this.idleRenderTasks.entries().next().value;
        if (!entry) return false;
        this.idleRenderTasks.delete(entry[0]);
        idleTask = entry[1];
        idleTask.prepare();
        if (this.disposed) return false;
      }

      // 在进入管线前消费旧请求；管线中再次 requestRender 会保留为下一帧的脏标记。
      this.renderRequested = false;
      this.renderAttempts += 1;
      rendered = options.renderFrame();
      if (!rendered) this.requestRender();
      else this.renderedFrames += 1;
      return rendered;
    } catch (error) {
      this.requestRender();
      throw error;
    } finally {
      // 不能跨帧保留临时 visible；异常、未就绪与同步销毁也必须恢复。
      try { idleTask?.finish(rendered); }
      finally { this.processingFrame = false; }
    }
  }

  /**
   * 消费当前任务快照；异常时保留失败及尚未执行的任务供下一帧重试。
   * @returns 本轮是否实际执行了视觉更新。
   */
  private flushVisualUpdates(): boolean {
    const reusableUpdates = this.activeVisualUpdates;
    this.activeVisualUpdates = this.pendingVisualUpdates;
    this.pendingVisualUpdates = reusableUpdates;
    let updated = false;
    try {
      for (const [owner, update] of this.activeVisualUpdates) {
        update();
        this.activeVisualUpdates.delete(owner);
        this.executedVisualUpdates += 1;
        updated = true;
      }
    } finally {
      if (!this.disposed) {
        // 新任务比失败快照更新，不能用失败前捕获的旧任务覆盖它。
        this.activeVisualUpdates.forEach((update, owner) => {
          if (!this.pendingVisualUpdates.has(owner)) this.pendingVisualUpdates.set(owner, update);
        });
      }
      this.activeVisualUpdates.clear();
    }
    return updated;
  }
}
